import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, X } from "lucide-react";
import * as LucideIcons from "lucide-react";
import Navigation from "@/components/Navigation";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import DiveCenterCard from "@/components/DiveCenterCard";
import HeroSection from "@/components/HeroSection";
import TimelineSidebar from "@/components/TimelineSidebar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import WaveDivider from "@/components/WaveDivider";
import GrainOverlay from "@/components/GrainOverlay";
import ScrollReveal from "@/components/ScrollReveal";
import { generateOrganizationSchema, generatePersonSchema, generateLocalBusinessSchema, generateTouristAttractionSchema } from "@/utils/structuredData";
import { useTheme } from "@/contexts/ThemeContext";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { pageStyles } = useTheme();
  const [currentLocation, setCurrentLocation] = useState(null);
  const [diveCenterInfoList, setDiveCenterInfoList] = useState([]);
  const [showCard, setShowCard] = useState(false);
  const [timelineMonths, setTimelineMonths] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [allDiveCenters, setAllDiveCenters] = useState([]);
  const [dynamicSections, setDynamicSections] = useState([]);
  
  // Get body theme
  const bodyTheme = pageStyles?.home?.body;
  const bodyBgType = bodyTheme?.background_type || 'color';
  const bodyBgValue = bodyTheme?.background_value || "#ffffff";
  const bodyTextColor = bodyTheme?.text_color || "#1e293b";
  const bodyAccent = bodyTheme?.accent_color || "#0891b2";
  const bodyAccentSecondary = bodyTheme?.accent_color_secondary || bodyAccent;
  const waveDecorations = bodyTheme?.wave_decorations === true;
  const bodyBorderColor = bodyTheme?.border_color || "rgba(229,231,235,1)";
  
  // Detect dark theme
  const isDarkBody = bodyTextColor === "#e2e8f0" || bodyTextColor === "#ffffff" || bodyTextColor === "#f8fafc";
  
  // Navbar ALWAYS overlays the hero, regardless of the active theme.
  // (Previously this was gated on the theme's nav_overlay flag, which caused
  // White Ocean to push the hero down while Kraken Dark overlaid it.)
  const navOverlay = true;
  
  const getBodyStyle = () => {
    // For dark themes with solid color bg, don't set section bg — 
    // let the body's tentacle pattern show through
    if (isDarkBody && bodyBgType === 'color') {
      return { color: bodyTextColor };
    }
    if (bodyBgType === 'color') {
      return { backgroundColor: bodyBgValue, color: bodyTextColor };
    } else if (bodyBgType === 'gradient') {
      if (bodyBgValue.includes('linear-gradient')) {
        return { background: bodyBgValue, color: bodyTextColor };
      } else {
        return { background: `linear-gradient(to right, ${bodyBgValue})`, color: bodyTextColor };
      }
    } else if (bodyBgType === 'image') {
      return { 
        backgroundImage: `url(${bodyBgValue})`,
        backgroundSize: 'cover',
        backgroundPosition: bodyTheme?.image_position || 'center',
        backgroundAttachment: 'fixed',
        color: bodyTextColor
      };
    } else if (bodyBgType === 'video') {
      // For video, we'll need special rendering
      return { position: 'relative', color: bodyTextColor };
    }
    return { backgroundColor: '#ffffff', color: bodyTextColor };
  };
  
  const bodyStyle = getBodyStyle();

  useEffect(() => {
    const initializeData = async () => {
      try {
        // Fetch all locations and dive centers for color mapping
        const [locationsRes, centersRes, timelineRes, sectionsRes] = await Promise.all([
          axios.get(`${API}/locations`).catch(() => ({ data: [] })),
          axios.get(`${API}/map/dive-centers`).catch(() => ({ data: [] })),
          axios.get(`${API}/timeline/home-view`).catch(() => ({ data: { months: [] } })),
          axios.get(`${API}/page-sections/home`).catch(() => ({ data: [] }))
        ]);
        
        setAllLocations(locationsRes.data || []);
        setAllDiveCenters(centersRes.data || []);
        setTimelineMonths(timelineRes.data.months || []);
        setDynamicSections(sectionsRes.data || []);

        // Fetch current location from timeline
        const currentMonth = timelineRes.data.months?.find(m => m.is_current);
        const centers = centersRes.data || [];
        if (currentMonth && currentMonth.location_id) {
          const locationCenters = centers.filter(c => c.location_id === currentMonth.location_id);
          if (locationCenters.length > 0) {
            setDiveCenterInfoList(locationCenters);
            setCurrentLocation(locationCenters[0]);
          }
        } else if (currentMonth && currentMonth.dive_center_id) {
          const match = centers.find(c => c.id === currentMonth.dive_center_id);
          if (match) {
            setDiveCenterInfoList([match]);
            setCurrentLocation(match);
          }
        } else {
          // Fallback to old current-location API if no location assigned
          const locationRes = await axios.get(`${API}/current-location`).catch(() => ({ data: null }));
          setCurrentLocation(locationRes.data);
          if (centers.length > 0) {
            setDiveCenterInfoList([centers[0]]);
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    initializeData();
  }, []);

  const handleTimelineClick = (month) => {
    if (!month.location_id && !month.dive_center_id) return;

    // Collect all location_ids for this month+year (handles split months)
    const locationIds = new Set();
    const dcIds = new Set();
    timelineMonths.forEach(m => {
      if (m.month === month.month && m.year === month.year) {
        if (m.location_id) locationIds.add(m.location_id);
        if (m.dive_center_id) dcIds.add(m.dive_center_id);
      }
    });

    // Find all dive centers at ALL matched locations
    let centers = allDiveCenters.filter(c => locationIds.has(c.location_id));

    // Fallback: include any directly-referenced dive centers
    if (centers.length === 0) {
      centers = allDiveCenters.filter(c => dcIds.has(c.id));
    } else {
      // Also add any directly-referenced centers not already included
      dcIds.forEach(dcId => {
        if (!centers.some(c => c.id === dcId)) {
          const match = allDiveCenters.find(c => c.id === dcId);
          if (match) centers.push(match);
        }
      });
    }

    if (centers.length > 0) {
      setDiveCenterInfoList(centers);
      setCurrentLocation(centers[0]);
      setShowCard(true);

      setTimeout(() => {
        const card = document.querySelector('[data-testid="dive-center-detail-section"]');
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  // Generate structured data
  const structuredData = [
    generateOrganizationSchema(),
    generatePersonSchema(),
    currentLocation && generateLocalBusinessSchema(currentLocation),
    currentLocation && generateTouristAttractionSchema(currentLocation)
  ].filter(Boolean);

  // Dynamic SEO description based on location
  const seoDescription = currentLocation 
    ? `Join George for incredible diving adventures in ${currentLocation.location}, ${currentLocation.country}. PADI courses, dive packages, and underwater exploration with a professional instructor. From beginner to advanced - experience the best dive sites.`
    : 'Join George for unforgettable diving adventures around the world. PADI certified courses, fun diving, and dive travel packages. From beginner to advanced - explore the underwater world with an experienced instructor.';

  return (
    <div className="min-h-screen">
      <SEO 
        title={currentLocation 
          ? `George Dives - Scuba Diving in ${currentLocation.location}, ${currentLocation.country} | PADI Courses & Dive Travel`
          : 'George Dives - Professional Dive Instructor & PADI Courses Worldwide'
        }
        description={seoDescription}
        keywords={['fun diving', 'dive center', 'underwater adventure', 'dive instructor certification']}
        location={currentLocation}
        structuredData={structuredData}
      />
      
      <Navigation />
      <FloatingUserMenu />

      {/* Timeline Sidebar — scroll-driven vertical timeline on the left */}
      {timelineMonths.length > 0 && (
        <TimelineSidebar
          months={timelineMonths}
          onMonthClick={handleTimelineClick}
          allLocations={allLocations}
          allDiveCenters={allDiveCenters}
        />
      )}

      {/* Main content - no top padding when nav overlays hero */}
      <div className={navOverlay ? "" : "pt-24"}>
        <div className="overflow-x-hidden">
          {/* Announcement Banner - positioned below nav in overlay mode */}
          <div className="relative">
            <div className={`${navOverlay ? 'absolute top-24 left-0 right-0' : 'absolute top-0 left-0 right-0'} z-20`}>
              <AnnouncementBanner />
            </div>
          <HeroSection pageName="home" />
          </div>

      {/* Wave Decoration Separator */}
      <WaveDivider variant="tentacle" />

      {/* Grain texture overlay for dark theme */}
      {isDarkBody && <GrainOverlay opacity={0.025} />}

      {/* Dynamic Content Sections — block-based */}
      {dynamicSections.filter(s => s.visible !== false).map((section) => {
        const blocks = section.blocks || [];
        const col0 = blocks.filter(b => b.column === 0).sort((a, b) => a.order - b.order);
        const col1 = blocks.filter(b => b.column === 1).sort((a, b) => a.order - b.order);
        const isTwoCol = section.layout === 'two_column' && col1.length > 0;

        const renderBlock = (block) => {
          switch (block.type) {
            case 'badge':
              return (
                <div key={block.id} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
                  style={{
                    border: `1px solid ${isDarkBody ? bodyAccentSecondary + '40' : '#cffafe'}`,
                    backgroundColor: isDarkBody ? bodyAccentSecondary + '15' : '#ecfeff',
                    color: isDarkBody ? bodyAccentSecondary : '#0e7490'
                  }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: bodyAccent }}></span>
                  <span className="text-sm font-semibold tracking-wide uppercase">{block.text}</span>
                </div>
              );
            case 'heading':
              return (
                <div key={block.id} className="mb-6">
                  <h2 className="text-3xl lg:text-4xl font-bold leading-tight" style={{ color: bodyTextColor }}>{block.text}</h2>
                  <div className="mt-3 w-16 h-1 rounded-full" style={{ background: `linear-gradient(to right, ${bodyAccent}, ${bodyAccentSecondary})` }}></div>
                </div>
              );
            case 'text':
              return <p key={block.id} className="text-base sm:text-lg leading-relaxed whitespace-pre-line mb-6" style={{ color: bodyTextColor, opacity: 0.8 }}>{block.text}</p>;
            case 'image': {
              const url = block.url?.startsWith('http') ? block.url : `${BACKEND_URL}${block.url}`;
              return (
                <div key={block.id} className="rounded-2xl overflow-hidden shadow-2xl relative" style={{ border: isDarkBody ? `1px solid ${bodyBorderColor}` : 'none' }}>
                  <img src={url} alt="" className="w-full h-auto object-cover" loading="lazy" />
                </div>
              );
            }
            case 'image_overlay': {
              const IconName = block.icon ? block.icon.charAt(0).toUpperCase() + block.icon.slice(1) : 'Users';
              const Icon = LucideIcons[IconName] || Users;
              const cornerClass = {
                'top-left': '-top-6 -left-6',
                'top-right': '-top-6 -right-6',
                'bottom-left': '-bottom-6 -left-6',
                'bottom-right': '-bottom-6 -right-6',
              }[block.corner || 'bottom-right'] || '-bottom-6 -right-6';
              return (
                <div key={block.id} className={`absolute ${cornerClass} text-white p-5 rounded-xl shadow-lg z-10`}
                  style={{ background: `linear-gradient(135deg, ${bodyAccent}, ${bodyAccentSecondary})` }}>
                  <Icon className="w-7 h-7 mb-1.5" />
                  <div className="text-sm font-bold">{block.title}</div>
                  {block.subtitle && <div className="text-xs opacity-80">{block.subtitle}</div>}
                </div>
              );
            }
            case 'stats':
              return (
                <div key={block.id} className={`grid grid-cols-${Math.min((block.items || []).length, 4)} gap-4 sm:gap-6 mt-2`}>
                  {(block.items || []).map((stat, si) => (
                    <div key={si} className="text-center p-4 rounded-xl" style={{
                      backgroundColor: isDarkBody ? '#1c1533' : '#ffffff',
                      border: `1px solid ${isDarkBody ? bodyBorderColor : '#f3f4f6'}`,
                      boxShadow: isDarkBody ? 'none' : '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                      <div className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: bodyAccent }}>{stat.value}</div>
                      <div className="text-xs sm:text-sm font-medium" style={{ color: bodyTextColor, opacity: 0.6 }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              );
            case 'divider':
              return <div key={block.id} className="my-4" style={{ borderTop: `1px solid ${bodyBorderColor}` }} />;
            case 'spacer':
              return <div key={block.id} className="h-6" />;
            default:
              return null;
          }
        };

        return (
          <ScrollReveal key={section.id} animation="fade-up" duration={800}>
          <section className="py-16 relative overflow-hidden" style={bodyStyle} data-testid={`dynamic-section-${section.id}`}>
            {bodyBgType === 'video' && bodyBgValue && (
              <>
                <video autoPlay loop={bodyTheme?.video_loop !== false} muted playsInline
                  className="absolute inset-0 w-full h-full object-cover -z-10">
                  <source src={bodyBgValue} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-black/40 -z-5"></div>
              </>
            )}
            <div className="container mx-auto px-4 max-w-6xl">
              {isTwoCol ? (
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <ScrollReveal animation="slide-right" delay={100}><div>{col0.map(renderBlock)}</div></ScrollReveal>
                  <ScrollReveal animation="slide-left" delay={300}><div className="relative">{col1.map(renderBlock)}</div></ScrollReveal>
                </div>
              ) : (
                (() => {
                  const sorted = blocks.sort((a, b) => a.order - b.order);
                  const rendered = [];
                  let i = 0;
                  while (i < sorted.length) {
                    const featureCards = [];
                    let j = i;
                    while (j < sorted.length - 1 && sorted[j].type === 'badge' && sorted[j + 1].type === 'text') {
                      featureCards.push({ badge: sorted[j], text: sorted[j + 1] });
                      j += 2;
                    }
                    if (featureCards.length >= 2) {
                      rendered.push(
                        <div key={`cards-${featureCards[0].badge.id}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4 mb-6">
                          {featureCards.map((card, ci) => (
                            <ScrollReveal key={card.badge.id} animation="fade-up" delay={ci * 150} duration={700}>
                            <div className="group relative p-6 rounded-2xl transition-all duration-300 hover:-translate-y-1" style={{
                              backgroundColor: isDarkBody ? '#1c1533' : '#ffffff',
                              border: `1px solid ${isDarkBody ? bodyBorderColor : '#f3f4f6'}`,
                              boxShadow: isDarkBody ? 'none' : '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={(e) => { 
                              e.currentTarget.style.borderColor = isDarkBody ? bodyAccentSecondary + '40' : '#a5f3fc';
                              e.currentTarget.style.boxShadow = isDarkBody ? `0 4px 20px ${bodyAccentSecondary}15` : '0 4px 12px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => { 
                              e.currentTarget.style.borderColor = isDarkBody ? bodyBorderColor : '#f3f4f6';
                              e.currentTarget.style.boxShadow = isDarkBody ? 'none' : '0 1px 3px rgba(0,0,0,0.05)';
                            }}
                            >
                              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{
                                border: `1px solid ${isDarkBody ? bodyAccentSecondary + '40' : '#cffafe'}`,
                                backgroundColor: isDarkBody ? bodyAccentSecondary + '15' : '#ecfeff',
                                color: isDarkBody ? bodyAccentSecondary : '#0e7490'
                              }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: bodyAccent }}></span>
                                <span className="text-xs font-semibold tracking-wide uppercase">{card.badge.text}</span>
                              </div>
                              <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: bodyTextColor, opacity: 0.75 }}>{card.text.text}</p>
                            </div>
                            </ScrollReveal>
                          ))}
                        </div>
                      );
                      i = j;
                    } else {
                      rendered.push(renderBlock(sorted[i]));
                      i++;
                    }
                  }
                  return <div>{rendered}</div>;
                })()
              )}
            </div>
          </section>
          </ScrollReveal>
        );
      })}

      {/* Dive Center Cards — shown when a timeline month is clicked */}
      {diveCenterInfoList.length > 0 && showCard && (() => {
        const grouped = [];
        const seen = new Set();
        diveCenterInfoList.forEach(center => {
          const key = center.location_id || center.id;
          if (!seen.has(key)) {
            seen.add(key);
            grouped.push({
              locationId: center.location_id,
              locationLabel: `${center.location}, ${center.country}`,
              centers: diveCenterInfoList.filter(c => (c.location_id || c.id) === key),
            });
          }
        });
        const multiLocation = grouped.length > 1;

        return (
          <section className="py-16 relative overflow-hidden" style={bodyStyle} data-testid="dive-center-detail-section">
            <div className="container mx-auto px-4 max-w-5xl">
              <div className="text-center mb-8 relative">
                <button onClick={() => setShowCard(false)} className="absolute right-0 top-0 p-2 rounded-full transition-colors" 
                  style={{ color: bodyTextColor, opacity: 0.5 }}
                  data-testid="close-dive-center-card">
                  <X className="w-5 h-5" />
                </button>
                <p className="text-sm font-semibold mb-2" style={{ color: bodyAccent }}>
                  {diveCenterInfoList.length > 1 ? 'PARTNER DIVE CENTERS' : 'PARTNER DIVE CENTER'}
                </p>
                {!multiLocation && (
                  <h3 className="text-3xl font-bold mb-2" style={{ color: bodyTextColor }}>
                    {grouped[0].locationLabel}
                  </h3>
                )}
              </div>

              <div className="space-y-10">
                {grouped.map((group, gIdx) => (
                  <div key={group.locationId || gIdx}>
                    {multiLocation && (
                      <div className="flex items-center gap-3 mb-4" data-testid={`location-group-${gIdx}`}>
                        <div className={`w-3 h-3 rounded-full shrink-0`} style={{ backgroundColor: gIdx === 0 ? bodyAccent : gIdx === 1 ? bodyAccentSecondary : '#10b981' }} />
                        <h3 className="text-xl font-bold" style={{ color: bodyTextColor }}>{group.locationLabel}</h3>
                        <div className="flex-1 h-px" style={{ backgroundColor: bodyBorderColor }} />
                      </div>
                    )}
                    <div className={group.centers.length > 1 ? 'space-y-6' : ''}>
                      {group.centers.map(center => (
                        <DiveCenterCard key={center.id} diveCenterInfo={center} onViewMap={() => navigate('/map')} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Wave Decoration Before Footer */}
      <WaveDivider variant="minimal" toColor={isDarkBody ? '#1a0a2e' : undefined} height="h-10 sm:h-16" />

      <Footer />
        </div>
      </div>
    </div>
  );
};

export default Home;
